<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { watch } from 'vue'
import * as yup from 'yup'
import { updateServiceCategoryAction } from '@/api/functions/settings/service-categories-action'
import { useToast } from '@/composables/useToast'

interface CategoryFormValues {
  title: string
  slug: string
  status: string
}

const props = defineProps({
  modelValue: Boolean,
  selectedCategory: { type: Object, required: true },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()

// --- Validation Schema ---
const schema = yup.object({
  title: yup
    .string()
    .required('عنوان دسته‌بندی الزامی است')
    .min(2, 'عنوان دسته‌بندی باید حداقل 2 کاراکتر باشد')
    .max(100, 'عنوان دسته‌بندی نمی‌تواند بیشتر از 100 کاراکتر باشد'),

  slug: yup
    .string()
    .required('اسلاگ الزامی است')
    .min(2, 'اسلاگ باید حداقل 2 کاراکتر باشد')
    .max(100, 'اسلاگ نمی‌تواند بیشتر از 100 کاراکتر باشد'),

  // glyph: yup
  //     .string()
  //     .required('آیکون الزامی است'),

  status: yup.string().required('وضعیت الزامی است'),
})

// --- Form setup ---
const { handleSubmit, errors, resetForm } = useForm<CategoryFormValues>({
  validationSchema: schema,
  initialValues: {
    title: '',
    slug: '',
    // glyph: '',
    status: 'ACTIVE',
  },
})

// Fields
const { value: title } = useField('title')
const { value: slug } = useField('slug')
// const { value: glyph } = useField('glyph');
const { value: status } = useField('status')

// --- Status options ---
const statusOptions = [
  { label: 'فعال', value: 'ACTIVE' },
  { label: 'غیرفعال', value: 'INACTIVE' },
]

// --- Watch selectedCategory and populate form ---
watch(
  () => props.selectedCategory,
  (category) => {
    if (category && category.id) {
      resetForm({
        values: {
          title: category.title || category.name || '',
          slug: category.slug || '',
          // glyph: category.glyph || '',
          status: category.status || 'ACTIVE',
        },
      })
    } else {
      resetForm()
    }
  },
  { immediate: true, deep: true }
)

// --- Submit ---
const onSubmit = handleSubmit(async (values) => {
  if (!props.selectedCategory?.id) {
    toast.error('دسته‌بندی انتخاب نشده است', 'خطا')
    return
  }

  try {
    await updateServiceCategoryAction(props.selectedCategory.id, {
      name: values.title,
      slug: values.slug,
      is_active: values.status === 'ACTIVE',
    })

    toast.success('دسته‌بندی با موفقیت ویرایش شد')

    emit('confirm')
    closeModal()
  } catch (error) {
    console.error('Update category error:', error)

    toast.error('ویرایش دسته‌بندی انجام نشد', 'خطا')
  }
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
        background="#FFF" :closable="true" title="ویرایش دسته‌بندی" closeDirection="left" class="update-request">

        <form @submit.prevent="onSubmit" class="flex flex-col min-h-[430px]">
            <div class="flex-1 pb-8">

                <div class="grid px-4 pt-6 pb-9 gap-6">

                    <Input v-model="title" name="title" label="عنوان دسته‌بندی" type="text" :error="errors.title" />

                    <Input v-model="slug" name="slug" label="اسلاگ" type="text" :error="errors.slug" />

                    <!-- <Input v-model="glyph" name="glyph" label="آیکون" type="text" :error="errors.glyph" /> -->

                    <Select v-model="status" name="status" label="وضعیت" :options="statusOptions"
                        :error="errors.status" />

                </div>
            </div>

            <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">

                <Button type="submit" label="ویرایش" :isAdmin="true" class="w-9/12 h-12 rounded-xl" />

                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    class="w-4/12 h-12 rounded-xl border-2" />

            </div>
        </form>
    </Modal>
</template>